import React, { useEffect, useRef, useState } from 'react';

import { X } from 'lucide-react';
import { useMutation, queryCache } from 'react-query';
import { Toast } from 'src/common/components';

import { bulkCreateInviteCodes, createInviteCode } from '../api';
import { InviteCodeSource } from '../types';
import { SOURCE_LABEL, SOURCES, toExpiryIso } from '../utils';

type Props = {
  onClose: () => void;
};

type Mode = 'single' | 'bulk';

const inputClass =
  'w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-2.0';

export const GenerateCodeModal: React.FC<Props> = ({ onClose }) => {
  const [mode, setMode] = useState<Mode>('single');
  const [quantity, setQuantity] = useState(10);
  const [maxUses, setMaxUses] = useState(1);
  const [expiresAt, setExpiresAt] = useState('');
  const [label, setLabel] = useState('');
  const [campaign, setCampaign] = useState('');
  const [source, setSource] = useState<InviteCodeSource>('admin');
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Move focus into the dialog on open so keyboard/AT users start inside it.
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // Close on Escape and keep Tab focus cycling within the dialog (focus trap).
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusable = Array.from(
      panelRef.current.querySelectorAll<HTMLElement>(
        'button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled'));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const invalidate = (): void => {
    queryCache.invalidateQueries('inviteCodes');
  };

  const [createSingle, { isLoading: creatingSingle }] = useMutation(createInviteCode, {
    onSuccess: () => {
      Toast.success('Invite code created.');
      invalidate();
      onClose();
    },
    onError: () => {
      Toast.error('Could not create the invite code.');
    },
  });

  const [createBulk, { isLoading: creatingBulk }] = useMutation(bulkCreateInviteCodes, {
    onSuccess: (res) => {
      Toast.success(`${res.data.created} invite codes created.`);
      invalidate();
      onClose();
    },
    onError: () => {
      Toast.error('Could not create the invite codes.');
    },
  });

  const isLoading = creatingSingle || creatingBulk;

  const handleSubmit = (): void => {
    const shared = {
      max_uses: maxUses,
      expires_at: toExpiryIso(expiresAt),
      label: label.trim() || undefined,
      campaign: campaign.trim() || undefined,
      source,
    };
    if (mode === 'bulk') {
      createBulk({ ...shared, count: quantity });
    } else {
      createSingle(shared);
    }
  };

  const invalidBulk = mode === 'bulk' && (quantity < 1 || quantity > 500);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="gen-codes-title"
      onKeyDown={handleKeyDown}
      onClick={onClose}
      className="bg-opacity-40 fixed inset-0 z-50 flex items-center justify-center p-4 bg-black"
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md p-6 bg-white rounded-lg shadow-xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 id="gen-codes-title" className="text-lg font-semibold">
            Generate invite codes
          </h3>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="hover:text-gray-700 text-gray-400"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="inline-flex mb-5 overflow-hidden text-sm border border-gray-200 rounded">
          {(['single', 'bulk'] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`px-4 py-1.5 transition-colors ${
                mode === m ? 'bg-primary-2.0 text-white' : 'bg-white hover:bg-gray-50 text-gray-700'
              }`}
            >
              {m === 'single' ? 'Single' : 'Bulk'}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {mode === 'bulk' && (
            <label className="block">
              <span className="block mb-1 text-sm font-medium text-gray-700">Quantity</span>
              <input
                type="number"
                min={1}
                max={500}
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className={inputClass}
              />
              {invalidBulk && (
                <span className="text-xs text-red-500">Enter a quantity between 1 and 500.</span>
              )}
            </label>
          )}

          <label className="block">
            <span className="block mb-1 text-sm font-medium text-gray-700">Max uses per code</span>
            <input
              type="number"
              min={1}
              value={maxUses}
              onChange={(e) => setMaxUses(Number(e.target.value))}
              className={inputClass}
            />
          </label>

          <label className="block">
            <span className="block mb-1 text-sm font-medium text-gray-700">
              Expires at <span className="text-gray-400">(optional)</span>
            </span>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className={inputClass}
            />
          </label>

          <label className="block">
            <span className="block mb-1 text-sm font-medium text-gray-700">
              Label <span className="text-gray-400">(optional)</span>
            </span>
            <input
              type="text"
              value={label}
              placeholder="e.g. Launch batch"
              onChange={(e) => setLabel(e.target.value)}
              className={inputClass}
            />
          </label>

          <label className="block">
            <span className="block mb-1 text-sm font-medium text-gray-700">
              Campaign <span className="text-gray-400">(optional)</span>
            </span>
            <input
              type="text"
              value={campaign}
              placeholder="e.g. instagram, launch"
              onChange={(e) => setCampaign(e.target.value)}
              className={inputClass}
            />
          </label>

          <label className="block">
            <span className="block mb-1 text-sm font-medium text-gray-700">Source</span>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value as InviteCodeSource)}
              className={inputClass}
            >
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {SOURCE_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="hover:bg-gray-50 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || invalidBulk || maxUses < 1}
            className="bg-primary-2.0 hover:bg-primary-3.0 disabled:opacity-50 px-4 py-2 text-sm text-white rounded"
          >
            {isLoading ? 'Creating…' : mode === 'bulk' ? `Create ${quantity} codes` : 'Create code'}
          </button>
        </div>
      </div>
    </div>
  );
};
